import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, IconButton, PageHeader, Pill, Select, Skeleton, useConfirm, scrollBehavior } from '../ui';
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, CloseIcon, PlusIcon, RefreshIcon, TrashIcon } from '../ui/icons';
import { sectionCrumb } from '../settings/SectionLayout';
import api from '../config/api';
import TmdbSearch, { sourceError } from './TmdbSearch';
import SourceSearch from './SourceSearch';
import { sourceLabel } from '../sources';
import FieldValue from './FieldValue';
import NotFound from './NotFound';
import { fieldOptionLabel, optionValue, uiLang } from '../kinds';
import { errorMessage } from '../errors';
import { useLeaveGuard } from '../shell/changes';

// The shapes a TMDB attribute can have (backend Sources/TmdbCatalogue): what transforms apply depends on them.
const baseShape = (attribute) => (attribute?.shape || '').split(':')[0];
const LIST_SHAPES = ['text_list', 'codes'];
const TEXT_TARGETS = ['text', 'textarea'];
const LIST_TARGETS = ['tags', 'checkbox'];
const VALUE_TARGETS = ['select', 'radio', 'checkbox', 'tags', 'vocab'];
const IMAGE_LANGUAGES = ['ui', 'null', 'fr', 'en', 'de', 'es', 'it', 'ja'];
const MEASURE_UNITS = ['min', 'h'];
const CAST_FORMATS = ['name', 'character', 'name_character', 'name_dash_character'];
const SEEN_LIMIT = 200;

let uid = 0;
const withUid = (mapping) => ({ ...mapping, uid: ++uid });

/** The values a field may receive in a value map: its options (codes shown with labels), a vocabulary's codes, or any word for tags. */
const targetChoices = (field, lang, vocabularies = {}) => (field.type === 'vocab'
  ? (vocabularies[field.vocabulary] || []).map(entry => ({ value: entry.code, label: entry.labels[lang] || entry.labels.en }))
  : (field.options || []).map(option => ({ value: optionValue(option), label: fieldOptionLabel(field, option, lang) })));

/** A coded select (a model's): its options are fixed, nothing can be added to them. */
const isCoded = (field) => field.type === 'select' && (field.options || []).some(option => option && typeof option === 'object');

/** Values seen in previews, per type, kept in this browser: the page proposes them in value maps. */
const seenKey = (typeId) => `inventorium.integration.seen.${typeId}`;
const loadSeen = (typeId) => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(seenKey(typeId)) || '{}');
    return stored && typeof stored === 'object' ? stored : {};
  } catch (e) {
    return {};
  }
};
const saveSeen = (typeId, seen) => {
  try {
    window.localStorage.setItem(seenKey(typeId), JSON.stringify(seen));
  } catch (e) {
    // private window or storage full: the values are only kept for this visit
  }
};

/** The source values a value map can list for an attribute: a vocabulary's codes, else what previews have shown. */
function sourceValuesOf(attribute, vocabularies, seen, lang) {
  if (baseShape(attribute) === 'codes') {
    const name = attribute.shape.slice(6);
    return (vocabularies[name] || []).map(entry => ({ value: entry.code, label: entry.labels[lang] || entry.labels.en }));
  }
  return (seen[attribute.id] || []).map(value => ({ value, label: value }));
}

/** "Correspondance des valeurs": source value → the field's value, ignored, or automatic; and what to do with the others. */
function ValueMapEditor({ mapping, attribute, field, sourceValues, readOnly, onChange, lang, vocabularies }) {
  const { t } = useTranslation();
  const [extra, setExtra] = useState('');
  // Values typed by hand stay listed (on "automatic") until the page is left, mapped or not.
  const [typed, setTyped] = useState([]);
  const values = mapping.values || {};
  const choices = targetChoices(field, lang, vocabularies);
  const listed = [...sourceValues];
  [...Object.keys(values), ...typed].forEach(value => {
    if (!listed.some(entry => entry.value === value)) listed.push({ value, label: value });
  });
  const setValue = (source, target) => {
    const next = { ...values };
    if (target === undefined) delete next[source];
    else next[source] = target;
    onChange({ ...mapping, values: next });
  };
  // A vocabulary's codes are fixed: what is not mapped is ignored.
  const policies = field.type === 'vocab' ? ['ignore'] : field.type === 'tags' || field.type === 'checkbox'
    ? ['add', 'ignore']
    : ['ignore', 'default', ...(isCoded(field) ? [] : ['add'])];
  const unmatched = mapping.unmatched || (field.type === 'tags' ? 'add' : 'ignore');

  return (
    <div className="value-map">
      {listed.length === 0 && <p className="settings-muted">{t('integration.values.noneYet')}</p>}
      {listed.length > 0 && (
        <table>
          <thead>
            <tr><th scope="col">{t('integration.values.source')}</th><th scope="col">{t('integration.values.target')}</th></tr>
          </thead>
          <tbody>
            {listed.map(({ value, label }) => {
              const current = values[value];
              const ignored = current === null;
              return (
                <tr key={value}>
                  <td>{label}{label !== value && <code className="settings-code"> {value}</code>}</td>
                  <td>
                    {field.type === 'tags' ? (
                      <span className="value-map__tag">
                        <input type="text" value={typeof current === 'string' ? current : ''} disabled={readOnly || ignored}
                          placeholder={t('integration.values.asIs')} aria-label={t('integration.values.targetFor', { value: label })}
                          onChange={(e) => setValue(value, e.target.value === '' ? undefined : e.target.value)} />
                        <label className="inline">
                          <input type="checkbox" checked={ignored} disabled={readOnly}
                            onChange={(e) => setValue(value, e.target.checked ? null : undefined)} />
                          {t('integration.values.ignore')}
                        </label>
                      </span>
                    ) : (
                      <select value={ignored ? '\u0000ignore' : (current ?? '')} disabled={readOnly}
                        aria-label={t('integration.values.targetFor', { value: label })}
                        onChange={(e) => setValue(value, e.target.value === '' ? undefined : e.target.value === '\u0000ignore' ? null : e.target.value)}>
                        <option value="">{t('integration.values.auto')}</option>
                        <option value={'\u0000ignore'}>{t('integration.values.ignore')}</option>
                        {choices.map(choice => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                      </select>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {!readOnly && baseShape(attribute) !== 'codes' && (
        <form className="inline-form" onSubmit={(e) => {
          e.preventDefault();
          const value = extra.trim();
          if (value && !typed.includes(value)) setTyped(prev => [...prev, value]);
          setExtra('');
        }}>
          <input type="text" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder={t('integration.values.addPlaceholder')}
            aria-label={t('integration.values.addPlaceholder')} />
          <Button type="submit" variant="secondary" size="sm" icon={PlusIcon} disabled={!extra.trim()}>{t('integration.values.add')}</Button>
        </form>
      )}
      <div className="inline-form">
        <label className="inline" htmlFor={`unmatched-${mapping.uid}`}>{t('integration.values.others')}</label>
        <select id={`unmatched-${mapping.uid}`} value={unmatched} disabled={readOnly}
          onChange={(e) => onChange({ ...mapping, unmatched: e.target.value, ...(e.target.value === 'default' ? { default: mapping.default || choices[0]?.value } : {}) })}>
          {policies.map(policy => <option key={policy} value={policy}>{t(`integration.values.policy.${policy}`)}</option>)}
        </select>
        {unmatched === 'default' && (
          <select value={mapping.default || ''} disabled={readOnly} aria-label={t('integration.values.default')}
            onChange={(e) => onChange({ ...mapping, default: e.target.value })}>
            {choices.map(choice => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

/** A crew row's filter: a job of TMDB's list, or a whole department, searchable, grouped by department. */
function JobPicker({ value, departments, failed, readOnly, onChange, lang, id }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const fold = (text) => String(text).toLowerCase();
  const q = fold(query.trim());
  const current = value?.job ? `job:${value.job}` : value?.department ? `department:${value.department}` : '';
  const known = departments.some(d => (value?.department && d.department === value.department) || (value?.job && d.jobs.includes(value.job)));
  // The chosen job or department stays listed, even when the search leaves it out.
  const visible = (d, jobs) => (value?.department === d.department) || (value?.job && jobs.includes(value.job));
  return (
    <div className="job-picker">
      <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} disabled={readOnly}
        placeholder={t('integration.crew.search')} aria-label={t('integration.crew.search')} />
      <select id={id} value={current} disabled={readOnly} aria-label={t('integration.crew.job')}
        onChange={(e) => {
          const [kind, ...rest] = e.target.value.split(':');
          onChange(e.target.value ? { [kind]: rest.join(':') } : null);
        }}>
        <option value="">{t('integration.crew.choose')}</option>
        {current && !known && <option value={current}>{value.job || value.department}</option>}
        {departments.map(d => {
          const label = d.labels?.[lang] || d.department;
          const departmentMatches = !q || fold(label).includes(q) || fold(d.department).includes(q);
          let jobs = departmentMatches ? d.jobs : d.jobs.filter(job => fold(job).includes(q));
          if (value?.job && d.jobs.includes(value.job) && !jobs.includes(value.job)) jobs = [value.job, ...jobs];
          // "All of the department" when the search names the department, or matches every one of its jobs.
          const whole = departmentMatches || jobs.length === d.jobs.length || value?.department === d.department;
          if (!whole && jobs.length === 0 && !visible(d, jobs)) return null;
          return (
            <optgroup key={d.department} label={label}>
              {whole && <option value={`department:${d.department}`}>{t('integration.crew.wholeDepartment', { department: label })}</option>}
              {jobs.map(job => <option key={job} value={`job:${job}`}>{job}</option>)}
            </optgroup>
          );
        })}
      </select>
      {failed && <p role="alert" className="ui-field__error">{t('integration.crew.jobsFailed')}</p>}
    </div>
  );
}

/** The transform controls a pair (attribute → field) takes. */
/** A contributors row's filter (books): one role of the catalogue's. */
function RolePicker({ value, roles, readOnly, onChange, lang, id }) {
  const { t } = useTranslation();
  return (
    <select id={id} value={value?.role || ''} disabled={readOnly} aria-label={t('integration.roles.label')}
      onChange={(e) => onChange(e.target.value ? { role: e.target.value } : null)}>
      <option value="">{t('integration.roles.choose')}</option>
      {(roles || []).map(role => <option key={role.code} value={role.code}>{role.labels[lang] || role.labels.en}</option>)}
    </select>
  );
}

function TransformControls({ mapping, attribute, field, readOnly, onChange }) {
  const { t } = useTranslation();
  const shape = baseShape(attribute);
  const transform = mapping.transform || {};
  const set = (key, value) => {
    const next = { ...transform };
    if (value === undefined || value === '' || value === false) delete next[key];
    else next[key] = value;
    onChange({ ...mapping, transform: next });
  };
  const controls = [];
  const textual = TEXT_TARGETS.includes(field.type);
  const listing = LIST_TARGETS.includes(field.type);
  if ((textual && LIST_SHAPES.includes(shape)) || (listing && shape === 'text')) {
    controls.push(
      <label key="separator" className="inline">
        {t(textual ? 'integration.transform.joinWith' : 'integration.transform.splitOn')}
        <input type="text" maxLength={10} size={4} value={transform.separator ?? ''} disabled={readOnly}
          placeholder={textual ? ', ' : ';'} onChange={(e) => set('separator', e.target.value)} />
      </label>
    );
  }
  if (attribute.id === 'cast' && (textual || listing)) {
    controls.push(
      <label key="format" className="inline">
        {t('integration.transform.format')}
        <select value={transform.format || 'name'} disabled={readOnly} onChange={(e) => set('format', e.target.value === 'name' ? undefined : e.target.value)}>
          {CAST_FORMATS.map(format => <option key={format} value={format}>{t(`integration.transform.formats.${format}`)}</option>)}
        </select>
      </label>
    );
  }
  if ((textual && LIST_SHAPES.includes(shape)) || listing) {
    controls.push(
      <label key="max" className="inline">
        {t('integration.transform.max')}
        <input type="number" min={1} max={textual ? 500 : 100} value={transform.max ?? ''} disabled={readOnly} className="native-controls__narrow"
          placeholder={t('integration.transform.all')} onChange={(e) => set('max', e.target.value === '' ? undefined : Number(e.target.value))} />
      </label>
    );
  }
  if (shape === 'date' && textual) {
    controls.push(
      <label key="date_part" className="inline">
        {t('integration.transform.datePart')}
        <select value={transform.date_part || 'full'} disabled={readOnly} onChange={(e) => set('date_part', e.target.value)}>
          <option value="full">{t('integration.transform.fullDate')}</option>
          <option value="year">{t('integration.transform.yearOnly')}</option>
        </select>
      </label>
    );
  }
  if (field.type === 'measure') {
    const units = (field.units || []).filter(unit => MEASURE_UNITS.includes(unit.toLowerCase()));
    controls.push(
      <label key="unit" className="inline">
        {t('integration.transform.unit')}
        <select value={transform.unit || units[0] || ''} disabled={readOnly} onChange={(e) => set('unit', e.target.value)}>
          {units.map(unit => <option key={unit} value={unit}>{unit}</option>)}
        </select>
      </label>
    );
  }
  if (shape === 'text' && textual && attribute.id !== 'title') {
    controls.push(
      <label key="skip" className="inline">
        <input type="checkbox" checked={!!transform.skip_if_title} disabled={readOnly} onChange={(e) => set('skip_if_title', e.target.checked)} />
        {t('integration.transform.skipIfTitle')}
      </label>
    );
  }
  return controls.length ? <div className="transform-controls">{controls}</div> : null;
}

/**
 * /settings/item-types/:id/integration — how TMDB fills this type's fields,
 * set by the type's owner: preferences, one row per attribute TMDB gives
 * (target field among those that can hold it, transforms, value maps), and a
 * preview of a real title through the unsaved settings. Readers of the type
 * see it read-only.
 */
function ItemTypeIntegration({ itemTypes }) {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const lang = uiLang(i18n);
  const { id } = useParams();
  const [view, setView] = useState(undefined);   // undefined: loading; null: none
  const [options, setOptions] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [openValues, setOpenValues] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);   // {kind: ok|error, text}
  const [picked, setPicked] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [seen, setSeen] = useState(() => loadSeen(id));
  const [errorRow, setErrorRow] = useState(null);   // {uid, text}: the row a refused save points at
  const [departments, setDepartments] = useState([]);   // TMDB's crew jobs, for crew rows
  const [jobsFailed, setJobsFailed] = useState(false);
  const [order, setOrder] = useState([]);   // the sources asked, in order (models with several: books)
  const itemType = (itemTypes || []).find(type => String(type.id) === String(id));

  const adopt = (data) => {
    setView(data);
    setOptions(data.config.options);
    setOrder(data.config.sources || [data.config.source]);
    setMappings(data.config.mappings.map(withUid));
    setDirty(false);
  };

  // The page's labels follow the UI language; a change of language reloads them without discarding unsaved edits.
  const dirtyRef = React.useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    let alive = true;
    api.get(`/item-types/${id}/integration`, { params: { lang } })
      .then(response => {
        if (!alive) return;
        if (dirtyRef.current) setView(response.data);
        else adopt(response.data);
      })
      .catch(() => { if (alive) setView(null); });
    return () => { alive = false; };
  }, [id, lang]);

  const hasCrew = !!view?.catalogue?.some(attribute => attribute.filter === true);   // TMDB's crew rows (books' are by role)
  useEffect(() => {
    if (!hasCrew) return undefined;
    let alive = true;
    api.get('/sources/tmdb/jobs')
      .then(response => { if (alive) { setDepartments(response.data.departments || []); setJobsFailed(false); } })
      .catch(() => { if (alive) setJobsFailed(true); });
    return () => { alive = false; };
  }, [hasCrew]);

  // Leaving with unsaved edits asks first: closing the tab, following a link
  // of the page or the shell, and the shell's own navigations (search box).
  const askToLeave = React.useCallback(() => confirm({
    title: t('integration.confirmLeaveTitle'),
    message: t('integration.confirmLeave'),
    confirmLabel: t('integration.leaveAnyway'),
    cancelLabel: t('integration.stay'),
    danger: true,
  }), [t, confirm]);
  useLeaveGuard(dirty, askToLeave);

  const fieldsByKey = useMemo(() => Object.fromEntries((view?.fields || []).map(field => [field.key, field])), [view]);
  const readOnly = !view?.can_manage;

  // Several sources (books): the page orders them; a film's config is sent as before. A model with one source other
  // than TMDB (video games: IGDB) is searched and previewed the generic way, its config keeping `source` alone.
  const multi = (view?.available_sources || []).length > 0;
  const generic = multi || (!!view && !!view.source && view.source !== 'tmdb');
  const config = () => ({
    source: generic ? order[0] : 'tmdb',
    ...(multi ? { sources: order } : {}),
    options,
    mappings: mappings.filter(mapping => mapping.field).map(({ uid: _uid, ...mapping }) => mapping),
  });

  const change = (updater) => {
    setMappings(updater);
    setDirty(true);
    setMessage(null);
    setErrorRow(null);
  };
  const setOption = (key, value) => {
    setOptions(prev => ({ ...prev, [key]: value }));
    setDirty(true);
    setMessage(null);
  };

  const errorText = (error) => {
    const data = error?.response?.data || {};
    if (data.code === 'invalid_mapping' && Number.isInteger(data.index)) {
      const mapping = config().mappings[data.index];
      const attribute = view.catalogue.find(a => a.id === mapping?.attribute);
      return t('integration.errors.invalidMapping', { attribute: attribute?.label || mapping?.attribute, error: data.error });
    }
    if (data.code === 'client_outdated') return t('addFromSource.errors.client_outdated');
    return t('integration.errors.generic', { error: errorMessage(t, error) });
  };

  const save = async () => {
    // A crew row needs both a job (or department) and a field: an incomplete one is pointed at, not dropped.
    const incomplete = mappings.find(m => view.catalogue.find(a => a.id === m.attribute)?.filter && (!m.field || !m.filter));
    if (incomplete) {
      setErrorRow({ uid: incomplete.uid, text: t(incomplete.field ? 'integration.crew.needsJob' : 'integration.crew.needsField') });
      setMessage({ kind: 'error', text: t('integration.errors.seeRow') });
      requestAnimationFrame(() => document.getElementById(`mapping-${incomplete.uid}`)?.scrollIntoView({ behavior: scrollBehavior(), block: 'center' }));
      return;
    }
    setSaving(true);
    try {
      const response = await api.put(`/item-types/${id}/integration`, config(), { params: { lang } });
      adopt(response.data);
      setErrorRow(null);
      setMessage({ kind: 'ok', text: t('integration.saved') });
    } catch (error) {
      // A refused mapping is shown on its row (the index counts the rows sent: those with a field).
      const index = error.response?.data?.index;
      const sent = mappings.filter(mapping => mapping.field);
      if (error.response?.data?.code === 'invalid_mapping' && Number.isInteger(index) && sent[index]) {
        setErrorRow({ uid: sent[index].uid, text: error.response.data.error });
        setMessage({ kind: 'error', text: t('integration.errors.seeRow') });
        requestAnimationFrame(() => document.getElementById(`mapping-${sent[index].uid}`)?.scrollIntoView({ behavior: scrollBehavior(), block: 'center' }));
      } else {
        setMessage({ kind: 'error', text: errorText(error) });
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!(await confirm({
      title: t('integration.confirmResetTitle'),
      message: t('integration.confirmReset'),
      confirmLabel: t('integration.resetButton'),
      danger: true,
    }))) return;
    setSaving(true);
    try {
      const response = await api.delete(`/item-types/${id}/integration`, { params: { lang } });
      adopt(response.data);
      setMessage({ kind: 'ok', text: t('integration.resetDone') });
    } catch (error) {
      setMessage({ kind: 'error', text: errorText(error) });
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async (result) => {
    if (!result) return;
    setPreviewing(true);
    setPreviewError(null);
    try {
      const response = await api.post(`/item-types/${id}/integration/preview`, generic
        ? { source: result.source, source_id: result.source_id, config: config() }
        : { tmdb_id: result.tmdb_id, config: config() });
      setPreview(response.data);
      // Text values TMDB gave, kept so value maps can list them.
      const next = { ...seen };
      Object.entries(response.data.attributes || {}).forEach(([attribute, value]) => {
        const shape = baseShape(view.catalogue.find(a => a.id === attribute));
        if (shape !== 'text' && shape !== 'text_list') return;
        const values = Array.isArray(value) ? value : [value];
        next[attribute] = [...new Set([...(next[attribute] || []), ...values.map(String)])].slice(-SEEN_LIMIT);
      });
      setSeen(next);
      saveSeen(id, next);
    } catch (error) {
      setPreview(null);
      setPreviewError(error.response?.data?.code === 'invalid_mapping' ? errorText(error) : sourceError(t, error));
    } finally {
      setPreviewing(false);
    }
  };

  if (view === undefined) return <Skeleton height={320} radius="var(--radius-lg)" />;
  if (view === null) return <NotFound />;

  const name = view.item_type_name || itemType?.name || '';
  // The words of a page about several sources, where TMDB's name it (integration.multi.*).
  const ti = (key, vars) => (generic && i18n.exists(`integration.multi.${key}`) ? t(`integration.multi.${key}`, vars) : t(`integration.${key}`, vars));
  const available = view.available_sources || [];
  const moveSource = (index, delta) => {
    const next = [...order];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    setOrder(next);
    setDirty(true);
    setMessage(null);
  };
  const setSources = (next) => {
    setOrder(next);
    setDirty(true);
    setMessage(null);
  };
  const sourceState = (source) => {
    if (!source.configured) return t('integration.sources.notConnected');
    if (source.busy_until && new Date(source.busy_until) > new Date()) {
      return t('integration.sources.pausedUntil', { time: new Date(source.busy_until).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }) });
    }
    return null;
  };
  const kind = view.preset === 'tv' ? 'tv' : 'movie';
  const taken = (except) => new Set(mappings.filter(m => m.uid !== except && m.field).map(m => m.field));
  const languages = options.poster_languages || [];
  const moveLanguage = (index, delta) => {
    const next = [...languages];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    setOption('poster_languages', next);
  };
  const languageLabel = (code) => t(`integration.languages.${code}`, { defaultValue: code });
  const warningText = (warning) => {
    const field = fieldsByKey[warning.field];
    return t(`integration.warnings.${warning.code}`, {
      field: field?.name || warning.field,
      attribute: view.catalogue.find(a => a.id === warning.attribute)?.label || warning.attribute,
      value: warning.value ?? '',
      reason: warning.reason ?? '',
    });
  };

  // The value map of a row: a toggle, then its editor.
  const valueMapToggle = (mapping, attribute, field, sourceValues, update) => (
    <>
      <Button variant="ghost" size="sm" iconRight={ChevronDownIcon} aria-expanded={!!openValues[mapping.uid]}
              className={`integration-values-toggle${openValues[mapping.uid] ? ' is-open' : ''}`}
              onClick={() => setOpenValues(prev => ({ ...prev, [mapping.uid]: !prev[mapping.uid] }))}>
        {t('integration.values.title')}
        {mapping.values && Object.keys(mapping.values).length > 0 && ` (${Object.keys(mapping.values).length})`}
      </Button>
      {openValues[mapping.uid] && (
        <ValueMapEditor mapping={mapping} attribute={attribute} field={field} readOnly={readOnly} lang={lang}
          sourceValues={sourceValues} onChange={update} vocabularies={view.vocabularies || {}} />
      )}
    </>
  );

  return (
    <div className="integration-page native-controls">
      <PageHeader
        breadcrumb={[sectionCrumb(t, 'settings'), { label: t('nav.itemTypes'), to: '/settings/item-types' }, { label: name }]}
        title={ti('title', { name })}
        subtitle={ti('intro')}
      >
        {view.is_default && <p className="integration-page__badge"><Pill tone="accent">{t('integration.isDefault')}</Pill></p>}
      </PageHeader>

      <div className="settings-stack">
        {readOnly && <Alert tone="info" role="note">{t('integration.readOnly')}</Alert>}

        <Card as="section" padding="lg" aria-labelledby="integration-preferences">
          <h2 id="integration-preferences" className="settings-card__title">{t('integration.preferences')}</h2>
          <div className="integration-prefs">
            <Select id="integration-data-language" label={t('integration.dataLanguage')} hint={ti('dataLanguageHelp')}
                    value={options.data_language || 'fr'} disabled={readOnly} className="integration-prefs__language"
                    onChange={(e) => setOption('data_language', e.target.value)}>
              <option value="fr">{t('integration.languages.fr')}</option>
              <option value="en">{t('integration.languages.en')}</option>
            </Select>
            {multi && (
              <fieldset className="choice-set integration-sources" disabled={readOnly}>
                <legend className="ui-field__label">{t('integration.sources.order')}</legend>
                <p className="ui-field__hint">{t('integration.sources.orderHelp')}</p>
                <ol className="language-order">
                  {order.map((sourceId, index) => {
                    const source = available.find(s => s.id === sourceId) || { id: sourceId, configured: true };
                    const state = sourceState(source);
                    const label = sourceLabel(sourceId, available, lang);
                    return (
                      <li key={sourceId}>
                        <span className="language-order__name">{label}{state && <span className="settings-muted"> — {state}</span>}</span>
                        <IconButton icon={ArrowUpIcon} size="sm" variant="ghost" onClick={() => moveSource(index, -1)} disabled={index === 0}
                          label={t('integration.moveUp', { language: label })} />
                        <IconButton icon={ArrowDownIcon} size="sm" variant="ghost" onClick={() => moveSource(index, 1)} disabled={index === order.length - 1}
                          label={t('integration.moveDown', { language: label })} />
                        <IconButton icon={CloseIcon} size="sm" variant="ghost" onClick={() => setSources(order.filter(s => s !== sourceId))}
                          disabled={order.length === 1} label={t('integration.sources.remove', { source: label })} />
                      </li>
                    );
                  })}
                </ol>
                {available.some(s => !order.includes(s.id)) && (
                  <Select label={t('integration.sources.add')} hideLabel value="" className="integration-prefs__add"
                          onChange={(e) => e.target.value && setSources([...order, e.target.value])}>
                    <option value="">{t('integration.sources.add')}</option>
                    {available.filter(s => !order.includes(s.id)).map(s => <option key={s.id} value={s.id}>{sourceLabel(s.id, available, lang)}</option>)}
                  </Select>
                )}
                <p className="ui-field__hint">{t('integration.sources.isbnRule')}</p>
              </fieldset>
            )}
            {multi && (
              <fieldset className="choice-set" disabled={readOnly}>
                <legend className="ui-field__label">{t('integration.sources.merge')}</legend>
                {['fill', 'first'].map(value => (
                  <label key={value} className="choice-set__option">
                    <input type="radio" name="merge" value={value} checked={(options.merge || 'fill') === value} onChange={() => setOption('merge', value)} />
                    {t(`integration.sources.mergeOption.${value}`)}
                  </label>
                ))}
              </fieldset>
            )}
            {'title_language' in options && (
            <fieldset className="choice-set" disabled={readOnly}>
              <legend className="ui-field__label">{t('integration.titleLanguage')}</legend>
              {['ui', 'original'].map(value => (
                <label key={value} className="choice-set__option">
                  <input type="radio" name="title_language" value={value} checked={options.title_language === value}
                    onChange={() => setOption('title_language', value)} />
                  {t(`integration.titleLanguageOption.${value}`)}
                </label>
              ))}
            </fieldset>
            )}
            {'poster_languages' in options && (
            <fieldset className="choice-set" disabled={readOnly}>
              <legend className="ui-field__label">{t('integration.posterLanguages')}</legend>
              <ol className="language-order">
                {languages.map((code, index) => (
                  <li key={code}>
                    <span className="language-order__name">{languageLabel(code)}</span>
                    <IconButton icon={ArrowUpIcon} size="sm" variant="ghost" onClick={() => moveLanguage(index, -1)} disabled={index === 0}
                      label={t('integration.moveUp', { language: languageLabel(code) })} />
                    <IconButton icon={ArrowDownIcon} size="sm" variant="ghost" onClick={() => moveLanguage(index, 1)} disabled={index === languages.length - 1}
                      label={t('integration.moveDown', { language: languageLabel(code) })} />
                    <IconButton icon={CloseIcon} size="sm" variant="ghost" onClick={() => setOption('poster_languages', languages.filter(c => c !== code))}
                      disabled={languages.length === 1} label={t('integration.removeLanguage', { language: languageLabel(code) })} />
                  </li>
                ))}
              </ol>
              {IMAGE_LANGUAGES.some(code => !languages.includes(code)) && (
                <Select label={t('integration.addLanguage')} hideLabel value="" className="integration-prefs__add"
                        onChange={(e) => e.target.value && setOption('poster_languages', [...languages, e.target.value])}>
                  <option value="">{t('integration.addLanguage')}</option>
                  {IMAGE_LANGUAGES.filter(code => !languages.includes(code)).map(code => <option key={code} value={code}>{languageLabel(code)}</option>)}
                </Select>
              )}
            </fieldset>
            )}
            <fieldset className="choice-set" disabled={readOnly}>
              <legend className="ui-field__label">{ti('fill')}</legend>
              {['empty_only', 'overwrite'].map(value => (
                <label key={value} className="choice-set__option">
                  <input type="radio" name="fill" value={value} checked={options.fill === value} onChange={() => setOption('fill', value)} />
                  {ti(`fillOption.${value}`)}
                </label>
              ))}
            </fieldset>
          </div>
        </Card>

        <Card as="section" padding="lg" aria-labelledby="integration-mapping">
          <h2 id="integration-mapping" className="settings-card__title">{ti('mapping')}</h2>
          <p className="settings-muted">{ti('mappingHelp')}</p>
          <div className="data-table-wrap">
            <table role="table" className="data-table data-table--stack integration-table">
              <thead>
                <tr role="row">
                  <th role="columnheader" scope="col">{ti('attribute')}</th>
                  <th role="columnheader" scope="col">{t('integration.field')}</th>
                  <th role="columnheader" scope="col">{t('integration.settings')}</th>
                </tr>
              </thead>
              <tbody>
                {view.catalogue.flatMap(attribute => {
                  const own = mappings.filter(m => m.attribute === attribute.id);
                  if (attribute.filter) {
                    // Crew rows: as many as wanted, each a job (or department) into a field of its own.
                    const crewRows = own.map((mapping, index) => {
                      const field = fieldsByKey[mapping.field];
                      const stale = mapping.field && (!field || field.unused || !attribute.compatible_fields.includes(mapping.field));
                      const free = attribute.compatible_fields.filter(key => !taken(mapping.uid).has(key));
                      const update = (next) => change(prev => prev.map(m => (m.uid === mapping.uid ? next : m)));
                      return (
                        <tr role="row" key={mapping.uid} id={`mapping-${mapping.uid}`} className={errorRow?.uid === mapping.uid ? 'row-error' : undefined}>
                          <th role="rowheader" scope="row">
                            {index === 0 ? attribute.label : <span className="settings-muted">↳ {attribute.label}</span>}
                            {attribute.filter === 'role' ? (
                              <RolePicker id={`job-${mapping.uid}`} value={mapping.filter} roles={attribute.roles} readOnly={readOnly} lang={lang}
                                onChange={(filter) => update({ ...mapping, filter })} />
                            ) : (
                              <JobPicker id={`job-${mapping.uid}`} value={mapping.filter} departments={departments} failed={jobsFailed} readOnly={readOnly} lang={lang}
                                onChange={(filter) => update({ ...mapping, filter })} />
                            )}
                          </th>
                          <td role="cell" data-label={t('integration.field')}>
                            <select value={mapping.field} disabled={readOnly} aria-label={t('integration.fieldFor', { attribute: attribute.label })}
                              onChange={(e) => update({ attribute: attribute.id, filter: mapping.filter, field: e.target.value, uid: mapping.uid })}>
                              <option value="">{t('integration.crew.chooseField')}</option>
                              {stale && <option value={mapping.field}>{t('integration.staleField', { field: field?.name || mapping.field })}</option>}
                              {attribute.compatible_fields.map(key => (
                                <option key={key} value={key} disabled={!free.includes(key)}>
                                  {fieldsByKey[key]?.name || key}{!free.includes(key) ? ` (${t('integration.fieldTaken')})` : ''}
                                </option>
                              ))}
                            </select>
                            {stale && <p className="ui-field__error">{t('integration.staleHelp')}</p>}
                            {errorRow?.uid === mapping.uid && <p role="alert" className="ui-field__error">{errorRow.text}</p>}
                            {!readOnly && (
                              <Button variant="ghost" size="sm" icon={TrashIcon} className="integration-remove"
                                onClick={() => change(prev => prev.filter(m => m.uid !== mapping.uid))}>
                                {t('integration.crew.remove')}
                              </Button>
                            )}
                          </td>
                          <td role="cell" data-label={t('integration.settings')}>
                            {field && !stale && (
                              <>
                                <TransformControls mapping={mapping} attribute={attribute} field={field} readOnly={readOnly} onChange={update} />
                                {VALUE_TARGETS.includes(field.type) && valueMapToggle(mapping, attribute, field, [], update)}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    });
                    if (!readOnly) {
                      crewRows.push(
                        <tr role="row" key={`add-${attribute.id}`} className="integration-table__add">
                          <th role="rowheader" scope="row">{own.length === 0 ? attribute.label : ''}</th>
                          <td role="cell" colSpan={2}>
                            <Button variant="secondary" size="sm" icon={PlusIcon}
                              onClick={() => change(prev => [...prev, withUid({ attribute: attribute.id, field: '', filter: null })])}>
                              {t('integration.crew.add')}
                            </Button>
                            <span className="settings-muted integration-table__help">{t(attribute.filter === 'role' ? 'integration.roles.help' : 'integration.crew.help')}</span>
                          </td>
                        </tr>
                      );
                    }
                    return crewRows;
                  }
                  const rows = own.length ? own : [{ uid: `none-${attribute.id}`, attribute: attribute.id, field: '' }];
                  return rows.map((mapping, index) => {
                    const field = fieldsByKey[mapping.field];
                    const stale = mapping.field && (!field || field.unused || !attribute.compatible_fields.includes(mapping.field));
                    const free = attribute.compatible_fields.filter(key => !taken(mapping.uid).has(key));
                    const update = (next) => change(prev => prev.map(m => (m.uid === mapping.uid ? next : m)));
                    return (
                      <tr role="row" key={mapping.uid} id={`mapping-${mapping.uid}`} className={errorRow?.uid === mapping.uid ? 'row-error' : undefined}>
                        <th role="rowheader" scope="row">
                          {index === 0 ? attribute.label : <span className="settings-muted">↳ {attribute.label}</span>}
                          <span className="integration-table__shape">{t(`integration.shape.${baseShape(attribute)}`)}</span>
                        </th>
                        <td role="cell" data-label={t('integration.field')}>
                          <select value={mapping.field} disabled={readOnly} aria-label={t('integration.fieldFor', { attribute: attribute.label })}
                            onChange={(e) => {
                              const key = e.target.value;
                              if (!own.length) {
                                if (key) change(prev => [...prev, withUid({ attribute: attribute.id, field: key })]);
                              } else if (!key) {
                                change(prev => prev.filter(m => m.uid !== mapping.uid));
                              } else {
                                update({ attribute: attribute.id, field: key, uid: mapping.uid });   // another field: its settings start afresh
                              }
                            }}>
                            <option value="">{t('integration.notImported')}</option>
                            {stale && <option value={mapping.field}>{t('integration.staleField', { field: field?.name || mapping.field })}</option>}
                            {attribute.compatible_fields.map(key => (
                              <option key={key} value={key} disabled={!free.includes(key)}>
                                {fieldsByKey[key]?.name || key}{!free.includes(key) ? ` (${t('integration.fieldTaken')})` : ''}
                              </option>
                            ))}
                          </select>
                          {stale && <p className="ui-field__error">{t('integration.staleHelp')}</p>}
                          {errorRow?.uid === mapping.uid && <p role="alert" className="ui-field__error">{errorRow.text}</p>}
                          {!readOnly && own.length > 0 && index === own.length - 1 && free.some(key => key !== mapping.field) && (
                            <Button variant="ghost" size="sm" icon={PlusIcon} className="integration-another"
                              onClick={() => change(prev => [...prev, withUid({ attribute: attribute.id, field: free.find(key => key !== mapping.field) })])}>
                              {t('integration.anotherField')}
                            </Button>
                          )}
                        </td>
                        <td role="cell" data-label={t('integration.settings')}>
                          {field && !stale && (
                            <>
                              <TransformControls mapping={mapping} attribute={attribute} field={field} readOnly={readOnly} onChange={update} />
                              {VALUE_TARGETS.includes(field.type)
                                && valueMapToggle(mapping, attribute, field, sourceValuesOf(attribute, view.vocabularies || {}, seen, lang), update)}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {!readOnly && (
          <div className={`item-form__bar integration-bar${dirty ? ' is-dirty' : ''}`}>
            <span className="item-form__status" aria-live="polite">{dirty ? t('integration.unsaved') : ''}</span>
            <Button variant="secondary" icon={RefreshIcon} onClick={reset} disabled={saving || view.is_default}>{t('integration.reset')}</Button>
            <Button onClick={save} disabled={!dirty} loading={saving}>{saving ? t('integration.saving') : t('integration.save')}</Button>
          </div>
        )}
        {message && <Alert tone={message.kind === 'error' ? 'danger' : 'success'}>{message.text}</Alert>}

        <Card as="section" padding="lg" aria-labelledby="integration-preview">
          <h2 id="integration-preview" className="settings-card__title">{t('integration.preview')}</h2>
          <p className="settings-muted">{ti('previewHelp')}</p>
          <div className="integration-preview">
            {generic ? (
              <SourceSearch itemType={{ id: view.item_type_id, preset: view.preset, sources: order, fields: view.fields }} lang={lang} selected={picked} autoFocus={false}
                listStyle={{ maxHeight: '22rem' }}
                onSelect={(result) => { setPicked(result); if (result) runPreview(result); else setPreview(null); }} />
            ) : (
              <TmdbSearch kind={kind} lang={lang} selected={picked} autoFocus={false} listStyle={{ maxHeight: '22rem' }}
                onSelect={(result) => { setPicked(result); if (result) runPreview(result); else setPreview(null); }} />
            )}
            <div aria-live="polite" className="integration-preview__result">
              {previewing && <p className="settings-muted">{ti('previewing')}</p>}
              {previewError && <Alert tone="danger">{previewError}</Alert>}
              {preview && !previewing && picked && (
                <>
                  <div className="integration-preview__head">
                    <strong>{picked.title}{picked.year ? ` (${picked.year})` : ''}</strong>
                    {multi && preview.sources && (
                      <span className="settings-muted">{t('integration.sources.from', { sources: preview.sources.map(s => sourceLabel(s, available, lang)).join(', ') })}</span>
                    )}
                    {dirty && <Pill tone="amber">{t('integration.previewUnsaved')}</Pill>}
                    <Button variant="secondary" size="sm" icon={RefreshIcon} onClick={() => runPreview(picked)}>{t('integration.previewAgain')}</Button>
                  </div>
                  <table className="data-table integration-preview__table">
                    <tbody>
                      {(view.fields || []).filter(field => preview.values[field.key] !== undefined || preview.images[field.key]).map(field => (
                        <tr key={field.key}>
                          <th scope="row">{field.name}</th>
                          <td>
                            {preview.images[field.key]
                              ? <img src={preview.images[field.key]} alt="" className="integration-preview__poster" />
                              : <FieldValue field={field} value={preview.values[field.key]} />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.warnings.length > 0 && (
                    <Alert tone="warning" title={t('integration.warningsTitle')}>
                      <ul className="alert-list">
                        {preview.warnings.map((warning, index) => <li key={index}>{warningText(warning)}</li>)}
                      </ul>
                    </Alert>
                  )}
                </>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ItemTypeIntegration;
